<template>
  <div class="header-navigation">
    <v-container>
      <!-- Mobile Layout (!mdAndUp) -->
      <template v-if="!mdAndUp">
        <div class="header-navigation mobile-header">
          <v-container>
            <v-row align="center" class="mobile-top-row">
              <v-col cols="auto">
                <h5 class="go-back text-white" @click="navigateToOverview">
                  Report Overview
                </h5>
              </v-col>
              <v-spacer></v-spacer>
              <v-col
                cols="auto"
                class="header-navigation-btn-container d-flex justify-end"
              >
                <v-btn
                  variant="text"
                  density="compact"
                  color="white"
                  append-icon="mdi-share-variant-outline"
                >
                  Share
                </v-btn>
                <v-btn
                  variant="text"
                  density="compact"
                  color="white"
                  append-icon="mdi-printer-outline"
                >
                  Print
                </v-btn>
              </v-col>
            </v-row>
            <!-- Grade Panel moved back inside the main container -->
            <v-row class="mobile-grades-row">
              <v-col cols="12">
                <v-expansion-panels
                  v-model="mobileGradeNavExpanded"
                  class="custom-panels mobile-grade-panels"
                  elevation="0"
                >
                  <v-expansion-panel elevation="0">
                    <v-expansion-panel-title
                      class="pa-0 mobile-grade-panel-title"
                    >
                      <div
                        class="d-flex justify-space-between align-center py-1 w-100 px-2"
                      >
                        <h5 class="text-white font-weight-bold">
                          Full Reports by Grade
                        </h5>
                      </div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text class="mobile-grade-panel-content">
                      <div class="grades-navigation mobile-grades-inside">
                        <router-link
                          v-for="grade in [
                            { display: 'K', route: 'kindergarten' },
                            { display: '1', route: 'first-grade' },
                            { display: '2', route: 'second-grade' },
                            { display: '3', route: 'third-grade' },
                            { display: '4', route: 'fourth-grade' },
                            { display: '5', route: 'fifth-grade' },
                            { display: '6', route: 'sixth-grade' },
                            { display: '7', route: 'seventh-grade' },
                          ]"
                          :key="grade.route"
                          :to="{
                            name: 'report-grade-overview',
                            params: {
                              id: reportId,
                              grade: grade.route,
                            },
                          }"
                          custom
                          v-slot="{ navigate, isActive }"
                        >
                          <v-btn
                            color="white"
                            class="grades-navigation-button font-weight-bold"
                            :class="{
                              'v-btn--active':
                                isActive || route.params.grade === grade.route,
                            }"
                            @click="navigate"
                            block
                          >
                            {{ grade.display }}
                          </v-btn>
                        </router-link>
                      </div>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>
          </v-container>
        </div>
        <!-- Removed separate container for mobile grades -->
      </template>

      <!-- Desktop Layout (mdAndUp) -->
      <template v-else>
        <v-row align="center">
          <v-col cols="2">
            <h5 class="go-back text-white" @click="navigateToOverview">
              Report Overview
            </h5>
          </v-col>
          <v-col cols="7" class="text-center">
            <div class="grades-navigation">
              <span class="grades-navigation-title">Full Reports:</span>
              <router-link
                v-for="grade in [
                  { display: 'K', route: 'kindergarten' },
                  { display: '1', route: 'first-grade' },
                  { display: '2', route: 'second-grade' },
                  { display: '3', route: 'third-grade' },
                  { display: '4', route: 'fourth-grade' },
                  { display: '5', route: 'fifth-grade' },
                  { display: '6', route: 'sixth-grade' },
                  { display: '7', route: 'seventh-grade' },
                ]"
                :key="grade.route"
                :to="{
                  name: 'report-grade-overview',
                  params: {
                    id: reportId,
                    grade: grade.route,
                  },
                }"
                custom
                v-slot="{ navigate, isActive }"
              >
                <v-btn
                  color="white"
                  class="grades-navigation-button font-weight-bold"
                  :class="{
                    'v-btn--active':
                      isActive || route.params.grade === grade.route,
                  }"
                  @click="navigate"
                >
                  {{ grade.display }}
                </v-btn>
              </router-link>
            </div>
          </v-col>
          <v-col cols="3" class="d-flex justify-end">
            <v-btn color="white" append-icon="mdi-share-variant-outline">
              Share
            </v-btn>
            <v-btn color="white" append-icon="mdi-printer-outline">
              Print
            </v-btn>
          </v-col>
        </v-row>
      </template>
    </v-container>
  </div>
</template>

<script setup lang="ts">
  import { useRoute, useRouter } from 'vue-router';
  import { useDisplay } from 'vuetify';
  import { ref, computed } from 'vue';

  const route = useRoute();
  const router = useRouter();
  const reportId = route.params.id;

  const display = useDisplay();
  const mdAndUp = computed(() => display.mdAndUp.value);
  const mobileGradeNavExpanded = ref<number | null>(null);

  function navigateToOverview() {
    if (reportId) {
      router.push({ name: 'report-overview', params: { id: reportId } });
    }
  }
</script>

<style lang="scss" scoped>
  .header-navigation {
    left: 0;
    width: 100%;
    background-color: rgb(var(--v-theme-primary));
    padding-bottom: 38px;

    .header-navigation-btn-container {
      margin-right: -20px;
    }

    .go-back {
      cursor: pointer;
    }

    .v-container {
      position: relative;
      z-index: 10;
    }

    &.mobile-header {
      top: 0;
      z-index: 10;
      padding-bottom: 10px;

      .v-container {
        position: relative;
        padding-top: 10px;
      }
    }

    .mobile-top-row {
      margin-bottom: 10px;
    }

    .mobile-grades-row {
      .v-col {
        padding-top: 0;
        padding-bottom: 0;
      }
    }
  }

  .custom-panels.mobile-grade-panels {
    background: transparent !important;
    box-shadow: none !important;
    border: 1px solid rgba(var(--v-theme-white), 0.5);
    border-radius: 4px;

    :deep(.v-expansion-panel) {
      background: transparent !important;
      border: none !important;

      .v-expansion-panel-title__icon {
        color: white !important;
      }
    }
    :deep(.v-expansion-panel-title) {
      min-height: unset !important;
      padding: 8px 12px !important;
      border: none !important;
    }
    :deep(.v-expansion-panel-text__wrapper) {
      padding: 8px;
      background-color: transparent !important;
    }
    :deep(.v-expansion-panel::after) {
      border: none !important;
    }
  }

  .grades-navigation.mobile-grades-inside {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;

    .grades-navigation-button {
      margin: 4px 0;
      justify-content: flex-start;
      background-color: rgba(var(--v-theme-white), 0.1);

      &.v-btn--active {
        background-color: rgba(var(--v-theme-white), 0.3);
      }
    }
  }

  .grades-navigation-title {
    color: rgb(var(--v-theme-white));
    margin-right: 12px;
  }

  .grades-navigation:not(.mobile-grades-inside) {
    display: flex;
    align-items: center;
    justify-content: center;

    .grades-navigation-button {
      margin: 0 4px;

      &.v-btn--active {
        background-color: rgba(var(--v-theme-white), 0.2);
      }
    }
  }
</style>
